import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ThemeContext } from '../context/ThemeContext';
import { getDestinationBySlug, getRelatedStories } from '../utils/dataLoader';
import Lightbox from '../components/Lightbox';

const DestinationDetail = () => {
  const { slug } = useParams();
  const { isFavorite, addToFavorites } = useContext(ThemeContext);
  const [destination, setDestination] = useState(null);
  const [relatedStories, setRelatedStories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      try {
        const dest = await getDestinationBySlug(slug);
        if (dest) {
          setDestination(dest);
          const stories = await getRelatedStories(dest.slug);
          setRelatedStories(stories);
        } else {
          setError('Destination not found');
        }
      } catch (error) {
        console.error('Error loading destination:', error);
        setError('Failed to load destination');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [slug]);

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const handleCloseLightbox = () => {
    setSelectedImage(null);
  };

  const handlePreviousImage = () => {
    if (destination && destination.images.length > 0) {
      const newIndex = currentImageIndex === 0 ? destination.images.length - 1 : currentImageIndex - 1;
      setCurrentImageIndex(newIndex);
      setSelectedImage(destination.images[newIndex]);
    }
  };

  const handleNextImage = () => {
    if (destination && destination.images.length > 0) {
      const newIndex = currentImageIndex === destination.images.length - 1 ? 0 : currentImageIndex + 1;
      setCurrentImageIndex(newIndex);
      setSelectedImage(destination.images[newIndex]);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !destination) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">Error</h2>
          <p className="text-secondary-600 dark:text-secondary-400 mb-8">{error || 'Destination not found'}</p>
          <Link to="/destinations" className="btn-primary">
            Back to Destinations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{destination.name}, {destination.country} - Travelogue</title>
        <meta name="description" content={destination.excerpt} />
      </Helmet>

      <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900">
        {/* Hero Section */}
        <div className="relative h-96 md:h-[500px] overflow-hidden">
          <img
            src={destination.images[0] || getPlaceholderImage(1920, 500)}
            alt={destination.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="text-4xl md:text-6xl font-bold mb-4 text-shadow-lg">
                  {destination.name}
                </h1>
                <p className="text-xl md:text-2xl text-shadow mb-6">
                  {destination.country}
                </p>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => addToFavorites(destination)}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                      isFavorite(destination.slug)
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                    }`}
                  >
                    {isFavorite(destination.slug) ? '❤️ Saved' : '🤍 Save to Favorites'}
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg p-8 mb-8"
              >
                <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6">
                  About {destination.name}
                </h2>
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <p className="text-secondary-700 dark:text-secondary-300 leading-relaxed">
                    {destination.longDescription}
                  </p>
                </div>
              </motion.div>

              {/* Gallery */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg p-8 mb-8"
              >
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-6">
                  Gallery
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {destination.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => handleImageClick(image, index)}
                      className="aspect-square overflow-hidden rounded-lg hover:opacity-80 transition-opacity duration-200"
                    >
                      <img
                        src={image || getPlaceholderImage(300, 300)}
                        alt={`${destination.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Attractions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg p-8"
              >
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-6">
                  Top Attractions
                </h2>
                <div className="space-y-4">
                  {destination.attractions.map((attraction, index) => (
                    <div key={index} className="border-l-4 border-primary-500 pl-4">
                      <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                        {attraction.name}
                      </h3>
                      <p className="text-secondary-700 dark:text-secondary-300">
                        {attraction.description}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Tags */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg p-6 mb-8"
              >
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {destination.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Related Stories */}
              {relatedStories.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg p-6"
                >
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                    Related Stories
                  </h3>
                  <div className="space-y-4">
                    {relatedStories.map((story) => (
                      <Link
                        key={story.slug}
                        to={`/stories/${story.slug}`}
                        className="block p-4 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors duration-200"
                      >
                        <h4 className="font-medium text-secondary-900 dark:text-white mb-2">
                          {story.title}
                        </h4>
                        <p className="text-sm text-secondary-600 dark:text-secondary-400">
                          {story.author} • {story.readingTime} min read
                        </p>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <Lightbox
            image={selectedImage}
            onClose={handleCloseLightbox}
            onPrevious={handlePreviousImage}
            onNext={handleNextImage}
            hasPrevious={currentImageIndex > 0}
            hasNext={currentImageIndex < destination.images.length - 1}
            currentIndex={currentImageIndex}
            totalImages={destination.images.length}
          />
        )}
      </div>
    </>
  );
};

// Helper function for placeholder image
const getPlaceholderImage = (width = 400, height = 300) => {
  return `https://via.placeholder.com/${width}x${height}/1e40af/ffffff?text=Travelogue`;
};

export default DestinationDetail;
